import { useState, useEffect } from "react";
import { Check, AlertTriangle, Loader2, WifiOff } from "lucide-react";
import { useApp } from "@/context/AppCtx";

type HeliosStatus = "idle" | "loading" | "connected" | "error";

export function HeliosStatusBadge() {
  const { heliosKey, backendConfig } = useApp();
  const [status, setStatus] = useState<HeliosStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const hasHelios = heliosKey.trim() || backendConfig?.heliusApiKeyConfigured;
    if (!hasHelios) {
      setStatus("idle");
      setErrorMessage(null);
      return;
    }

    setStatus("loading");
    setErrorMessage(null);

    // Simulate a connection check - in production this would make an actual RPC call
    const checkConnection = async () => {
      try {
        // In a real implementation, this would make a test RPC call to Helius
        // For now, we'll simulate a successful connection if a key is present
        await new Promise(resolve => setTimeout(resolve, 500));
        setStatus("connected");
      } catch (err) {
        setStatus("error");
        setErrorMessage(err instanceof Error ? err.message : "Connection failed");
      }
    };

    checkConnection();
  }, [heliosKey]);

  if (status === "idle") {
    return (
      <div className="flex items-center gap-2 rounded-full border border-[var(--color-border-subtle)] bg-[var(--color-fill)] px-3 py-1.5">
        <WifiOff className="size-3.5 text-[var(--color-fg-dim)]" />
        <span className="text-[10px] font-medium text-[var(--color-fg-dim)]">Helios not configured</span>
      </div>
    );
  }

  if (status === "loading") {
    return (
      <div className="flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/[0.05] px-3 py-1.5">
        <Loader2 className="size-3.5 animate-spin text-amber-400" />
        <span className="text-[10px] font-medium text-amber-300">Checking Helius…</span>
      </div>
    );
  }

  if (status === "connected") {
    return (
      <div className="flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/[0.08] px-3 py-1.5">
        <Check className="size-3.5 text-emerald-400" />
        <span className="text-[10px] font-medium text-emerald-300">Helius connected</span>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/[0.08] px-3 py-1.5" title={errorMessage || "Helius connection error"}>
        <AlertTriangle className="size-3.5 text-red-400" />
        <span className="text-[10px] font-medium text-red-300">Helius error</span>
      </div>
    );
  }

  return null;
}
